/**
 * Modal de Perfil do Usuário
 * Permite edição de nome e upload de avatar
 * Avatar armazenado em: anexos/users/{user_id}/avatar.{ext}
 */
import { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Loader2, Camera, User } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { getStorageUrl } from '@/lib/storage';
import { logger } from '@/lib/logger';

interface UserProfileDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  userEmail: string;
  currentName: string | null;
  currentAvatarPath: string | null;
  onSuccess: () => void;
}

// Tipos de imagem aceitos
const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

/**
 * Extrai as iniciais do nome ou email do usuário
 */
const getInitials = (name: string): string => {
  if (!name) return 'U';
  if (name.includes('@')) {
    return name.split('@')[0].charAt(0).toUpperCase();
  }
  return name
    .split(' ')
    .filter(word => word.length > 0)
    .map(word => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

export function UserProfileDialog({
  open,
  onOpenChange,
  userId,
  userEmail,
  currentName,
  currentAvatarPath,
  onSuccess,
}: UserProfileDialogProps) {
  // Estados do formulário
  const [fullName, setFullName] = useState(currentName || '');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  
  // Ref para input de arquivo
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Resetar estados quando dialog abre
  useEffect(() => {
    if (open) {
      setFullName(currentName || '');
      setPreviewUrl(null);
      setSelectedFile(null);
    }
  }, [open, currentName]);

  // Limpar URL de preview ao desmontar
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  /**
   * Manipula seleção de arquivo
   */
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validar tipo
    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      toast.error('Tipo de arquivo inválido', {
        description: 'Apenas JPG, PNG e WebP são aceitos.',
      });
      return;
    }

    // Validar tamanho
    if (file.size > MAX_FILE_SIZE) {
      toast.error('Arquivo muito grande', {
        description: 'O tamanho máximo é 5MB.',
      });
      return;
    }

    // Limpar preview anterior
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    // Criar preview e salvar arquivo
    const newPreviewUrl = URL.createObjectURL(file);
    setPreviewUrl(newPreviewUrl);
    setSelectedFile(file);
  };

  /**
   * Salva alterações no perfil
   */
  const handleSave = async () => {
    if (!userId) return;

    setSaving(true);
    try {
      let newAvatarPath = currentAvatarPath;

      // Upload do avatar se um novo arquivo foi selecionado
      if (selectedFile) {
        const fileExt = selectedFile.name.split('.').pop()?.toLowerCase() || 'jpg';
        const storagePath = `users/${userId}/avatar.${fileExt}`;

        logger.info('[UserProfile] Uploading avatar to:', storagePath);

        // Upload com upsert para sobrescrever anterior
        const { error: uploadError } = await supabase.storage
          .from('anexos')
          .upload(storagePath, selectedFile, {
            upsert: true,
            contentType: selectedFile.type,
          });

        if (uploadError) {
          logger.error('[UserProfile] Upload error:', uploadError.message);
          throw new Error('Erro ao fazer upload da imagem');
        }

        newAvatarPath = storagePath;
      }

      // Atualizar perfil no banco
      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          full_name: fullName.trim() || null,
          avatar_url: newAvatarPath,
          updated_at: new Date().toISOString(),
        })
        .eq('id', userId);

      if (updateError) {
        logger.error('[UserProfile] Update error:', updateError.message);
        throw new Error('Erro ao atualizar perfil');
      }

      toast.success('Perfil atualizado com sucesso');
      onSuccess();
      onOpenChange(false);
    } catch (error) {
      logger.error('[UserProfile] Save error:', error);
      toast.error('Erro ao salvar', {
        description: error instanceof Error ? error.message : 'Tente novamente',
      });
    } finally {
      setSaving(false);
    }
  };

  // URL do avatar para exibição (preview ou atual)
  const displayAvatarUrl = previewUrl || getStorageUrl(currentAvatarPath);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Meu Perfil
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Avatar com opção de upload */}
          <div className="flex flex-col items-center gap-3">
            <div className="relative group">
              <Avatar className="h-24 w-24 border-2 border-border">
                <AvatarImage src={displayAvatarUrl} alt="Avatar" />
                <AvatarFallback className="bg-primary/10 text-primary text-2xl font-medium">
                  {getInitials(fullName || userEmail)}
                </AvatarFallback>
              </Avatar>
              
              {/* Overlay de câmera */}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                disabled={saving}
              >
                <Camera className="h-8 w-8 text-white" />
              </button>
            </div>

            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="text-sm text-primary hover:underline"
              disabled={saving}
            >
              Alterar foto
            </button>

            {/* Input de arquivo oculto */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleFileSelect}
              className="hidden"
            />

            {selectedFile && (
              <p className="text-xs text-muted-foreground">
                Nova imagem selecionada: {selectedFile.name}
              </p>
            )}
          </div>

          {/* Campo de Nome */}
          <div className="space-y-2">
            <Label htmlFor="fullName">Nome</Label>
            <Input
              id="fullName"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Seu nome completo"
              disabled={saving}
            />
          </div>

          {/* Campo de Email (somente leitura) */}
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              value={userEmail}
              disabled
              className="bg-muted"
            />
            <p className="text-xs text-muted-foreground">
              O email não pode ser alterado.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Salvando...
              </>
            ) : (
              'Salvar'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
